// ── WebLLM Backend ──────────────────────────────────────────────────────────
// In-browser LLM inference via WebGPU. Primary recommended backend.
// Uses @mlc-ai/web-llm — must be installed as a dependency.

import type { AIBackend, AIBackendStatus, AIBackendType, AIModelOption } from '../types';

// Dynamic import to avoid breaking builds if web-llm isn't installed
let webllmModule: typeof import('@mlc-ai/web-llm') | null = null;

const STORAGE_KEY = 'velostruct-ai-webllm-model';
const DEFAULT_MODEL = 'Qwen2.5-1.5B-Instruct-q4f16_1-MLC';

export const WEBLLM_MODELS: AIModelOption[] = [
  { id: 'Qwen2.5-0.5B-Instruct-q4f16_1-MLC', label: 'Qwen2.5 0.5B', sizeHint: '~0.4 GB', recommended: false },
  { id: 'Qwen2.5-1.5B-Instruct-q4f16_1-MLC', label: 'Qwen2.5 1.5B', sizeHint: '~1 GB', recommended: true },
  { id: 'Qwen2.5-3B-Instruct-q4f16_1-MLC', label: 'Qwen2.5 3B', sizeHint: '~2 GB' },
  { id: 'Llama-3.2-1B-Instruct-q4f16_1-MLC', label: 'Llama 3.2 1B', sizeHint: '~0.8 GB' },
  { id: 'Llama-3.2-3B-Instruct-q4f16_1-MLC', label: 'Llama 3.2 3B', sizeHint: '~2 GB' },
  { id: 'Phi-3.5-mini-instruct-q4f16_1-MLC', label: 'Phi-3.5 Mini', sizeHint: '~2.4 GB' },
  { id: 'gemma-2-2b-it-q4f16_1-MLC', label: 'Gemma 2 2B', sizeHint: '~1.6 GB' },
];

function storedModel(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_MODEL;
  } catch {
    return DEFAULT_MODEL;
  }
}

export class WebLLMBackend implements AIBackend {
  readonly type: AIBackendType = 'webllm';
  readonly contextWindowTokens = 8192;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private engine: any = null;
  private _ready = false;
  private _model = storedModel();

  async checkAvailability(): Promise<AIBackendStatus> {
    if (!('gpu' in navigator)) return 'unavailable';
    try {
      const adapter = await (navigator as Navigator & { gpu: { requestAdapter(): Promise<unknown> } }).gpu.requestAdapter();
      if (!adapter) return 'unavailable';
    } catch {
      return 'unavailable';
    }
    if (this._ready) return 'ready';
    return 'downloadable';
  }

  async listModels(): Promise<AIModelOption[]> {
    return WEBLLM_MODELS;
  }

  getSelectedModel(): string | null {
    return this._model;
  }

  setSelectedModel(id: string): void {
    if (id === this._model) return;
    this.destroy();
    this._model = id;
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
  }

  async initialize(onProgress?: (pct: number) => void): Promise<void> {
    if (this._ready) return;

    if (!webllmModule) {
      webllmModule = await import('@mlc-ai/web-llm');
    }

    const initProgressCallback = (report: { progress: number; text: string }) => {
      onProgress?.(report.progress);
    };

    this.engine = await webllmModule.CreateMLCEngine(this._model, {
      initProgressCallback,
    });
    this._ready = true;
  }

  async prompt(
    systemPrompt: string,
    userMessage: string,
    _responseSchema?: object,
    history: Array<{ role: 'user' | 'assistant'; content: string }> = [],
  ): Promise<string> {
    if (!this.engine) throw new Error('WebLLM engine not initialized');

    const response = await this.engine.chat.completions.create({
      messages: [
        { role: 'system', content: systemPrompt },
        ...history,
        { role: 'user', content: userMessage },
      ],
      temperature: 0.3,
      max_tokens: 2048,
      response_format: { type: 'json_object' },
    });

    return response.choices[0]?.message?.content ?? '{"operations":[],"reply":"No response generated."}';
  }

  destroy(): void {
    if (this.engine) {
      try { this.engine.unload?.(); } catch { /* ignore */ }
      this.engine = null;
      this._ready = false;
    }
  }
}
